// MyWorkerSwitch.jsx - the signed-in agent's own auto worker (core 3662): their PC, extension version, portal logins
// and Start / Pause auto mode. Shown at the top of the Desk home's right rail and on My attendance (owner 2026-10-20).
//
// ⚠️ SAYS ONLY WHAT WAS CHECKED (Desk home audit 2026-10-21):
//   * the headline is the SETTING ("auto mode on / paused"), never "running now" - no read here proves a PC is working;
//   * "current version" appears only after a SUCCESSFUL comparison with the published build. A failed or pending check
//     is "not verified", and Start waits for it (owner 2026-10-20: an old extension must not run) - Pause never waits;
//   * a failed read shows a failure + Retry instead of hiding the panel; a failed Start/Pause says so and keeps the
//     last confirmed setting;
//   * a poll that STARTED before a Start/Pause is dropped when it lands (it used to repaint the old mode after a
//     successful Pause);
//   * no polling while the tab is hidden; coming back refreshes at once.
import { useEffect, useRef, useState } from 'react'
import { t, tLog } from '../lib/i18n.js'
import { fmtTbilisi } from '../lib/tbilisiTime.js'
import { getMyWorker, setMyWorker, setMyWorkerPart, getExtSessions, getExtensionLatest } from '../db/core.js'
import { MyResharePools } from './MyResharePools.jsx'

// "20.10.11.55" (the published build) and "20101155" (what a browser reports) -> the same comparable number
const verKey = v => {
  const s = String(v || '')
  const parts = s.includes('.') ? s.split('.').map(x => x.padStart(2, '0')) : (s.match(/\d{2}/g) || [])
  return parts.length === 4 ? Number(parts[1] + parts[0] + parts[2] + parts[3]) : 0   // month, day, hour, minute
}
const PORTALS = [['ss', 'SS.ge'], ['myhome', 'Myhome'], ['korter', 'Korter']]
const POLL_MS = 60_000
const LATEST_TTL_MS = 30 * 60_000   // a build published while the panel is open is noticed within half an hour
const clock = iso => fmtTbilisi(iso, { hour: '2-digit', minute: '2-digit', hour12: false })

export function MyWorkerSwitch({ onSummary = null } = {}) {
  const [st, setSt] = useState(null)
  const [stErr, setStErr] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionErr, setActionErr] = useState(false)
  const [pcs, setPcs] = useState(null)          // the agent's own browsers (ext_sessions heartbeat)
  const [pcsErr, setPcsErr] = useState(false)
  const [pcsAt, setPcsAt] = useState(null)      // when the PC list was last read successfully
  const [latest, setLatest] = useState({ state: 'checking', v: null, at: 0 })   // the published extension build
  const gen = useRef(0)                         // bumped by every Start/Pause: older reads are discarded
  const latestRef = useRef(latest)
  latestRef.current = latest
  const loadRef = useRef(() => {})

  useEffect(() => {
    let gone = false
    const load = () => {
      const g = gen.current
      getMyWorker()
        .then(r => { if (gone || g !== gen.current) return; if (r && r.mode) { setSt(r); setStErr(false) } else setStErr(true) })
        .catch(() => { if (!gone && g === gen.current) setStErr(true) })
      getExtSessions()
        .then(r => { if (gone) return; setPcs(r?.sessions || []); setPcsErr(false); setPcsAt(new Date().toISOString()) })
        .catch(() => { if (!gone) setPcsErr(true) })
      const l = latestRef.current
      if (l.state !== 'ok' || Date.now() - l.at > LATEST_TTL_MS) {
        getExtensionLatest()
          .then(r => { if (!gone) setLatest(r?.version ? { state: 'ok', v: r.version, at: Date.now() } : { state: 'failed', v: null, at: Date.now() }) })
          .catch(() => { if (!gone) setLatest(x => ({ ...x, state: x.state === 'ok' ? 'ok' : 'failed', at: Date.now() })) })
      }
    }
    loadRef.current = load
    load()
    const id = setInterval(() => { if (document.visibilityState === 'visible') load() }, POLL_MS)
    const onVis = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVis)
    return () => { gone = true; clearInterval(id); document.removeEventListener('visibilitychange', onVis) }
  }, [])

  // one line of it for the phone's "next up" strip (IntakeTab) - the same reads, no second poll
  const summaryPc = (pcs || []).some(x => x.online)
  useEffect(() => {
    if (onSummary) onSummary(st ? { paused: !!(st.reshare_off && st.wa_off), pcOnline: summaryPc } : null)
  }, [st?.reshare_off, st?.wa_off, !!st, summaryPc])   // eslint-disable-line react-hooks/exhaustive-deps
  if (!st) {
    // a stable shell instead of nothing: loading, or a failure with Retry (never an absent panel)
    return (
      <div className="rounded-lg border border-border px-3 py-2 text-sm" aria-live="polite">
        <span className="font-medium">{t('my_worker_title')}</span>
        {stErr
          ? <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{t('my_worker_read_failed')}{' '}
              <button type="button" onClick={() => loadRef.current()} className="underline">{t('my_worker_retry')}</button></p>
          : <p className="text-xs text-muted-foreground mt-0.5">{t('my_worker_loading')}</p>}
      </div>
    )
  }
  // ⚠️ owner 2026-10-20: "it must say details about agent - if they installed the current version or not; if not we should
  // not allow to use it; if their accounts are logged in, if not tell her to login". The online browser that heartbeats
  // last is the one the automation runs on.
  const pc = (pcs || []).slice().sort((a, b) => (b.online - a.online) || (Date.parse(b.last_seen || 0) - Date.parse(a.last_seen || 0)))[0] || null
  const verified = latest.state === 'ok' && !!pc?.ext_version
  const outdated = !!(pc && latest.state === 'ok' && verKey(pc.ext_version) < verKey(latest.v))
  const locked = !pc || !pc.online || outdated || !verified
  // TWO SWITCHES (owner 2026-10-29 "two buttons reshare and wa, not three", core 4036): 🔁 reposts and 💬 WhatsApp are the
  // whole state. Turning one OFF is always allowed; turning one ON needs an online PC on a VERIFIED current extension
  // (owner 2026-10-20: an old extension must not run), and an agent with no WhatsApp line yet joins it first (core 3696 -
  // the server enrolls on a non-paused mode write).
  const togglePart = async part => {
    const key = part === 'reshare' ? 'reshare_off' : 'wa_off'
    const turningOn = !!st[key]
    if (busy || (turningOn && locked)) return
    gen.current++
    setBusy(true); setActionErr(false)
    try {
      if (turningOn && !st.lane) { const e = await setMyWorker('running'); if (e && e.mode) setSt(e) }
      const r = await setMyWorkerPart(part, !st[key])
      if (r && r.mode) setSt(s => ({ ...s, ...r })); else setActionErr(true)
    } catch { setActionErr(true) }
    finally { setBusy(false) }
  }
  const allOff = !!(st.reshare_off && st.wa_off)
  return (
    <div className="rounded-lg border border-border px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-medium">{t('my_worker_title')}</span>
        {/* only when it is true of BOTH parts (owner 2026-10-29 "notes and buttons are true"); a mix is told by the switches */}
        {(allOff || (!st.reshare_off && !st.wa_off)) && (
          <span className={`text-xs ${allOff ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {allOff ? t('my_worker_now_paused') : t('my_worker_now_running')}
          </span>)}
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{t('my_worker_hint')}</p>
      <div className="mt-2 rounded-md bg-muted/40 px-2 py-1.5 text-xs space-y-0.5">
        {pcsErr && (
          <p className="text-amber-700 dark:text-amber-400">
            {pcsAt ? tLog('my_worker_pcs_failed', { t: clock(pcsAt) }) : t('my_worker_pcs_failed_first')}{' '}
            <button type="button" onClick={() => loadRef.current()} className="underline">{t('my_worker_retry')}</button>
          </p>
        )}
        {pcs === null && !pcsErr && <p className="text-muted-foreground">{t('my_worker_loading')}</p>}
        {pcs !== null && !pc && <p className="text-amber-700 dark:text-amber-400">{t('my_worker_pc_none')}</p>}
        {pc && (
          <>
            <p>
              <span className="font-medium">{t('my_worker_pc')}:</span> {pc.device_name}{' · '}
              <span className={pc.online ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}>
                {pc.online ? t('my_worker_pc_online') : t('my_worker_pc_offline')}</span>
              {!pc.online && pc.last_seen && <span className="text-muted-foreground"> · {tLog('my_worker_pc_seen', { t: clock(pc.last_seen) })}</span>}
              {verified && !outdated && <span className="text-muted-foreground"> · v{pc.ext_version} · {t('my_worker_ver_ok')}</span>}
            </p>
            {outdated && <p className="text-red-600 dark:text-red-400 font-medium">
              {t('my_worker_ver_old').replace('{v}', pc.ext_version || '?').replace('{latest}', latest.v)}</p>}
            {!verified && (
              <p className="text-amber-700 dark:text-amber-400">
                {latest.state === 'checking' ? t('my_worker_ver_checking') : t('my_worker_ver_unknown')}
                {latest.state === 'failed' && <>{' '}<button type="button" onClick={() => loadRef.current()} className="underline">{t('my_worker_retry')}</button></>}
              </p>
            )}
            {PORTALS.map(([k, label]) => {
              const a = pc.portals?.[k] || {}
              // logged_in null = the PC has not reported this portal (ext_sessions keeps unknown), not "signed out"
              const known = a.logged_in === true || a.logged_in === false
              const ok = a.logged_in === true
              return (
                <p key={k} className={ok ? '' : known ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}>
                  {label}: {ok ? `${t('my_worker_login_ok')}${a.name ? ` (${a.name})` : ''}` : known ? t('my_worker_login_no').replace('{p}', label) : t('my_worker_login_unknown')}
                </p>
              )
            })}
          </>
        )}
      </div>
      <div className="mt-3 rounded-lg border border-border overflow-hidden">
        {[['reshare', 'reshare_off', 'my_worker_part_reshare', '🔁'], ['wa', 'wa_off', 'my_worker_part_wa', '💬'],
          // core 4280 (owner 2026-11-05): price edits & removals on this PC - off sends removals to an office Mac, edits wait
          ['edits', 'edits_off', 'my_worker_part_edits', '✏️']].map(([part, key, label, icon], i) => {
          const on = !st[key]
          return (
            <div key={part} className={`flex items-center justify-between gap-3 px-3 py-2 ${i ? 'border-t border-border' : ''}`}>
              <div className="min-w-0">
                <div className="text-sm"><span aria-hidden="true">{icon}</span> {t(label)}</div>
                <div className={`text-[11px] ${on ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
                  {on ? t('my_worker_part_on') : t('my_worker_part_off')}
                </div>
              </div>
              <button type="button" role="switch" aria-checked={on} disabled={busy || (!on && locked)} onClick={() => togglePart(part)}
                title={t(`my_worker_part_hint_${part}`)} aria-label={t(label)}
                className={`relative shrink-0 h-5 w-9 rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-emerald-600' : 'bg-amber-500'}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>
          )
        })}
      </div>
      <p className="text-xs mt-1 min-h-0" aria-live="polite">
        {actionErr && <span className="text-red-600 dark:text-red-400">{t('my_worker_save_failed')}</span>}
      </p>
      {st.enroll && !st.enroll.enrolled && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t(`my_worker_enroll_${st.enroll.why}`) !== `my_worker_enroll_${st.enroll.why}` ? t(`my_worker_enroll_${st.enroll.why}`) : (st.enroll.message || st.enroll.why)}</p>}
      {stErr && <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">{t('my_worker_read_stale')}</p>}
      {/* owner 2026-10-24 "will that be pickable here": what the automatic reposting shares, inside this same box */}
      <MyResharePools embedded />
    </div>
  )
}
